import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { AllFilterType, AppliedFilter, FilterListType } from '../../CommonComponents/custom_filter/types';
import { showToastMessage } from '../../../utils/commonFunctions';
import { handleLockApiError } from '../../../utils/lockErrorHandler';
import { SESSION_STATUS_LABEL } from '../constants/validateDetails.constants';
import {
  extractApiError,
  getEligibleSatellites,
  getSetupsBySatellite,
  getGroundStationProviders,
  getSetupConfig,
  getSessionConfig,
  getValidationSessions,
  deleteSession,
  deleteSetup,
  endSession,
} from '../api/validate.api';
import type {
  ConfigOption,
  DeleteTarget,
  SatelliteOption,
  ValidateSession,
  ValidateSetup,
} from '../types/validateDetails.types';
import type { GroundStationProvider } from '../types/validateDetails.types';
import type { ValidateContextValue } from '../context/validateContext.types';

const BACKGROUND_POLL_INTERVAL_MS = 30_000;
const DEPLOY_POLL_INTERVAL_MS     = 5_000;
const POLL_TIMEOUT_MS             = 120_000; // 2 min

const FILTER_OPERATOR = {
  enabledSearch: false,
  multiselection: false,
  placeholder: 'Operator',
  options: [
    { label: '= Equals', value: '= Equals' },
    { label: '≠ not Equals', value: '≠ not Equals' },
  ],
};

const normalizeFilterValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : '—';
};

const buildFilterOptions = (values: Array<string | undefined | null>) => {
  const unique = Array.from(new Set(values.map(normalizeFilterValue)));
  return unique.sort().map((value) => ({ label: value, value }));
};

const buildFilterItem = (label: string, options: { label: string; value: string }[]) => ({
  label,
  value: label,
  operator: FILTER_OPERATOR,
  properties: { enabledSearch: true, multiselection: true, placeholder: 'Value', options },
});

const getValidateSessionFilterList = (sessions: ValidateSession[]): FilterListType | null => {
  if (!sessions.length) return null;

  return {
    enabledSearch: true,
    placeholder: 'Property',
    options: [
      buildFilterItem('Title',               buildFilterOptions(sessions.map((s) => s.sessionName))),
      buildFilterItem('Testing Environment', buildFilterOptions(sessions.map((s) => s.testingEnvironment))),
      buildFilterItem('Setup',               buildFilterOptions(sessions.map((s) => s.setupName))),
      buildFilterItem('Teams',               buildFilterOptions(sessions.map((s) => s.teamName))),
      buildFilterItem('Status',              buildFilterOptions(sessions.map((s) => SESSION_STATUS_LABEL[s.status] ?? s.status))),
    ],
  };
};

const getSessionFieldValue = (session: ValidateSession, property: string) => {
  switch (property) {
    case 'Title':
      return normalizeFilterValue(session.sessionName);
    case 'Testing Environment':
      return normalizeFilterValue(session.testingEnvironment);
    case 'Setup':
      return normalizeFilterValue(session.setupName);
    case 'Teams':
      return normalizeFilterValue(session.teamName);
    case 'Status':
      return SESSION_STATUS_LABEL[session.status] ?? normalizeFilterValue(session.status);
    default:
      return '';
  }
};

const applySessionAdvanceFilters = (
  sessions: ValidateSession[],
  filters?: AppliedFilter[],
): ValidateSession[] => {
  if (!filters || filters.length === 0) return sessions;
  return filters.reduce((acc, filter) => {
    if (!filter.properties || !filter.operator || !filter.value) return acc;
    const values = (Array.isArray(filter.value) ? filter.value : [filter.value])
      .map((value) => (typeof value === 'string' ? value.trim() : String(value)))
      .map((value) => value.toLowerCase())
      .filter((value) => value.length > 0);
    if (values.length === 0) return acc;

    return acc.filter((session) => {
      const matchValue = getSessionFieldValue(session, filter.properties).toLowerCase();
      const hasMatch = values.some((value) => value === matchValue);
      return filter.operator === '≠ not Equals' ? !hasMatch : hasMatch;
    });
  }, sessions);
};

const isSamePendingSession = (pending: ValidateSession, session: ValidateSession) => (
  pending.isPendingCreate &&
  pending.setupId === session.setupId &&
  pending.sessionName.trim().toLowerCase() === session.sessionName.trim().toLowerCase() &&
  pending.teamName.trim().toLowerCase() === session.teamName.trim().toLowerCase()
);

export function useValidateDetails(): ValidateContextValue {
  const navigate = useNavigate();
  const { mission_name, mission_id } = useParams<{ mission_name: string; mission_id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Capture URL params at mount so async effects never read stale closures.
  const initialSatFromUrl   = useRef(searchParams.get('sat'));
  const initialSetupFromUrl = useRef(searchParams.get('setup'));

  // ── State — all declarations first ────────────────────────────────────────
  const [satelliteOptions,  setSatelliteOptions]  = useState<SatelliteOption[]>([]);
  const [selectedSatellite, setSelectedSatellite] = useState<SatelliteOption | null>(null);
  const [loadingSatellites, setLoadingSatellites] = useState(true);

  const [setups,        setSetups]        = useState<ValidateSetup[]>([]);
  const [loadingSetups, setLoadingSetups] = useState(true);
  const [activeSetupId, setActiveSetupId] = useState<string | null>(null);
  const [sessions,        setSessions]        = useState<ValidateSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const sessionsRef = useRef<ValidateSession[]>([]);

  const [allFilters, setAllFilters] = useState<AllFilterType>({});

  const [isCreateModalOpen,    setIsCreateModalOpen]    = useState(false);
  const [groundStationOptions, setGroundStationOptions] = useState<GroundStationProvider[]>([]);
  const [loadingProviders,     setLoadingProviders]     = useState(false);
  const [modelTypeOptions,     setModelTypeOptions]     = useState<ConfigOption[]>([]);
  const [loadingSetupConfig,   setLoadingSetupConfig]   = useState(false);

  const [isCreateSessionModalOpen,  setIsCreateSessionModalOpen]  = useState(false);
  const [testingEnvironmentOptions, setTestingEnvironmentOptions] = useState<ConfigOption[]>([]);
  const [loadingSessionConfig,      setLoadingSessionConfig]      = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deployPollingActive, setDeployPollingActive] = useState(false);
  const [deployPollingTick, setDeployPollingTick] = useState(0);

  // Deploy polling context: keep polling until the active setup reflects the new session.
  const deployPollingSetupIdRef = useRef<string | null>(null);
  // Baseline session IDs captured at deploy start — any session NOT in this set is the new one.
  const deployPollingBaselineSessionIdsRef = useRef<Set<string>>(new Set());

  // Prevent overlapping polling requests
  const sessionsPollInFlightRef = useRef(false);
  const endingSessionIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  // ── Memos (after state declarations) ──────────────────────────────────────
  const satStorageKey = useMemo(
    () => (mission_id ? `validate:selectedSatellite:${mission_id}` : null),
    [mission_id],
  );

  const setupStorageKey = useMemo(() => {
    const gid = selectedSatellite?.value?.groupId;
    return mission_id && gid ? `validate:selectedSetup:${mission_id}:${gid}` : null;
  }, [mission_id, selectedSatellite?.value?.groupId]);

  const fetchSetupsAndPreserveSelection = useCallback(async (opts?: { showErrors?: boolean; clearOnError?: boolean }) => {
    if (!mission_id || !selectedSatellite) return;
    const showErrors = opts?.showErrors ?? false;
    const clearOnError = opts?.clearOnError ?? false;
    const { data, apiError } = await getSetupsBySatellite(mission_id, selectedSatellite.value.groupId, selectedSatellite.value.satId);
    if (apiError) {
      if (showErrors) showToastMessage('error', apiError);
      if (clearOnError) {
        setSetups([]);
        setActiveSetupId(null);
      }
      return;
    }
    setSetups(data);
    setActiveSetupId((current) => {
      if (current && data.some((s) => s.setupId === current)) return current;
      let nextId: string | null = data[0]?.setupId ?? null;
      if (initialSetupFromUrl.current && data.some((s) => s.setupId === initialSetupFromUrl.current)) {
        nextId = initialSetupFromUrl.current;
      }
      if (setupStorageKey) {
        try {
          const saved = window.sessionStorage.getItem(setupStorageKey);
          if (saved && data.some((s) => s.setupId === saved)) nextId = saved;
        } catch (err) { console.warn('sessionStorage read failed', err); }
      }
      return nextId;
    });
  }, [mission_id, selectedSatellite, setupStorageKey]);

  const fetchSessionsForSatellite = useCallback(async (opts?: { showErrors?: boolean; clearOnError?: boolean }) => {
    if (!mission_id || !selectedSatellite) return [];
    const showErrors = opts?.showErrors ?? false;
    const clearOnError = opts?.clearOnError ?? false;
    const { data, apiError } = await getValidationSessions(
      mission_id,
      selectedSatellite.value.groupId,
      selectedSatellite.value.satId,
    );
    if (apiError) {
      if (showErrors) showToastMessage('error', apiError);
      if (clearOnError) setSessions([]);
      return [];
    }
    const endingSessionIdsToClear = new Set<string>();
    const displayData = data.map((session) => {
      if (!endingSessionIdsRef.current.has(session.sessionId)) return session;
      const sessionStillEnding = ['RUNNING', 'DEPLOYING', 'SCHEDULED'].includes(session.status);
      if (sessionStillEnding) return { ...session, isDeleting: true };
      endingSessionIdsToClear.add(session.sessionId);
      return session;
    });
    endingSessionIdsToClear.forEach((sessionId) => {
      endingSessionIdsRef.current.delete(sessionId);
    });
    endingSessionIdsRef.current.forEach((sessionId) => {
      if (!data.some((session) => session.sessionId === sessionId)) {
        endingSessionIdsRef.current.delete(sessionId);
      }
    });
    setSessions((prev) => {
      const pendingSessions = prev.filter((session) =>
        session.isPendingCreate &&
        !displayData.some((updatedSession) => isSamePendingSession(session, updatedSession))
      );
      return [...pendingSessions, ...displayData];
    });
    return displayData;
  }, [mission_id, selectedSatellite]);

  const pollSessionsForSatellite = useCallback(async (opts?: { showErrors?: boolean }) => {
    if (sessionsPollInFlightRef.current) return sessionsRef.current;
    sessionsPollInFlightRef.current = true;
    try {
      return await fetchSessionsForSatellite(opts);
    } catch {
      return sessionsRef.current;
    } finally {
      sessionsPollInFlightRef.current = false;
    }
  }, [fetchSessionsForSatellite]);

  const refreshSessionsForSatellite = useCallback(
    () => pollSessionsForSatellite({ showErrors: true }),
    [pollSessionsForSatellite],
  );

  const refreshSetupsForSatellite = useCallback(
    () => fetchSetupsAndPreserveSelection({ showErrors: true }),
    [fetchSetupsAndPreserveSelection],
  );

  // ── Fetch: satellites ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!mission_id) return;
    let cancelled = false;

    (async () => {
      setLoadingSatellites(true);
      try {
        const options = await getEligibleSatellites(mission_id);
        if (cancelled) return;
        setSatelliteOptions(options);
        setSelectedSatellite((prev) => {
          if (prev) {
            const stillExists = options.find((o) => o.value.groupId === prev.value.groupId);
            if (stillExists) return stillExists;
          }
          if (initialSatFromUrl.current) {
            const fromUrl = options.find((o) => o.value.groupId === initialSatFromUrl.current);
            if (fromUrl) return fromUrl;
          }
          if (satStorageKey) {
            try {
              const saved = window.sessionStorage.getItem(satStorageKey);
              const match = saved ? options.find((o) => o.value.groupId === saved) : undefined;
              if (match) return match;
            } catch (err) { console.warn('sessionStorage read failed', err); }
          }
          return options[0] ?? null;
        });
      } catch (err) {
        if (cancelled) return;
        showToastMessage('error', extractApiError(err, 'Failed to fetch satellites'));
        setSatelliteOptions([]);
        setSelectedSatellite(null);
      } finally {
        if (!cancelled) setLoadingSatellites(false);
      }
    })();

    return () => { cancelled = true; };
  }, [mission_id, satStorageKey]);

  // ── Fetch: setups ──────────────────────────────────────────────────────────
  // Depends only on the satellite groupId primitive, not the full object,
  // to avoid redundant fetches when the object reference changes but the id does not.
  useEffect(() => {
    if (!mission_id || !selectedSatellite) return;
    let cancelled = false;
    setLoadingSetups(true);

    (async () => {
      try {
        await fetchSetupsAndPreserveSelection({ showErrors: true, clearOnError: true });
      } finally {
        if (!cancelled) setLoadingSetups(false);
      }
    })();

    return () => { cancelled = true; };
  // Intentionally depends on groupId primitive instead of selectedSatellite object or
  // fetchSetupsAndPreserveSelection to avoid redundant fetches when only the reference changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission_id, selectedSatellite?.value?.groupId]);

  // ── Fetch: sessions ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!mission_id || !selectedSatellite) return;
    let cancelled = false;
    setLoadingSessions(true);

    (async () => {
      try {
        await fetchSessionsForSatellite({ showErrors: true, clearOnError: true });
      } finally {
        if (!cancelled) setLoadingSessions(false);
      }
    })();

    return () => { cancelled = true; };
  // Intentionally depends on groupId primitive; selected satellite object may be recreated.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission_id, selectedSatellite?.value?.groupId]);

  // ── Poll: sessions in the background without flooding the network ─────────
  useEffect(() => {
    if (!mission_id || !selectedSatellite?.value?.groupId) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (deployPollingActive) return;
      await pollSessionsForSatellite();
    };

    const id = window.setInterval(poll, BACKGROUND_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [mission_id, selectedSatellite?.value?.groupId, deployPollingActive, pollSessionsForSatellite]);

  // ── Fetch: GS providers + model types (parallel, on Create Setup open) ────
  useEffect(() => {
    if (!isCreateModalOpen || !mission_id) return;
    const satId = selectedSatellite?.value?.satId || selectedSatellite?.value?.groupId;
    if (!satId) {
      setGroundStationOptions([]);
      setModelTypeOptions([]);
      return;
    }
    let cancelled = false;

    (async () => {
      setLoadingProviders(true);
      setLoadingSetupConfig(true);

      const [gsResult, configResult] = await Promise.allSettled([
        getGroundStationProviders(satId),
        getSetupConfig(mission_id, satId),
      ]);

      if (cancelled) return;

      if (gsResult.status === 'fulfilled') {
        if (gsResult.value.apiError) showToastMessage('error', gsResult.value.apiError);
        setGroundStationOptions(gsResult.value.data);
      }
      if (configResult.status === 'fulfilled') {
        if (configResult.value.apiError) showToastMessage('error', configResult.value.apiError);
        setModelTypeOptions(configResult.value.data);
      }

      setLoadingProviders(false);
      setLoadingSetupConfig(false);
    })();

    return () => { cancelled = true; };
  }, [isCreateModalOpen, mission_id, selectedSatellite?.value?.satId, selectedSatellite?.value?.groupId]);

  // ── Fetch: testing environments (on Create Session open) ──────────────────
  useEffect(() => {
    if (!isCreateSessionModalOpen || !mission_id) return;
    const satId = selectedSatellite?.value?.satId || selectedSatellite?.value?.groupId;
    if (!satId) { setTestingEnvironmentOptions([]); return; }
    let cancelled = false;

    (async () => {
      setLoadingSessionConfig(true);
      const { data, apiError } = await getSessionConfig(mission_id, satId);
      if (cancelled) return;
      if (apiError) showToastMessage('error', apiError);
      setTestingEnvironmentOptions(data);
      setLoadingSessionConfig(false);
    })();

    return () => { cancelled = true; };
  }, [isCreateSessionModalOpen, mission_id, selectedSatellite?.value?.satId, selectedSatellite?.value?.groupId]);

  // ── Poll: sessions while deploy in progress ───────────────────────────────
  useEffect(() => {
    if (!deployPollingActive || !mission_id || !selectedSatellite?.value?.groupId) return;
    const startMs = Date.now();
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - startMs >= POLL_TIMEOUT_MS) {
        setDeployPollingActive(false);
        return;
      }
      const latestSessions = await pollSessionsForSatellite();
      if (cancelled) return;

      const targetSetupId = deployPollingSetupIdRef.current ?? activeSetupId;
      const activeSessions = targetSetupId
        ? latestSessions.filter((session) => session.setupId === targetSetupId)
        : latestSessions;

      // Find the session that was just created (not present in the baseline snapshot).
      const newSession = activeSessions.find(
        (sess) => !deployPollingBaselineSessionIdsRef.current.has(sess.sessionId),
      );

      if (newSession) {
        // Stop only once the session has fully transitioned past deploying/scheduled.
        const isFinal =
          newSession.status === 'RUNNING' ||
          newSession.status === 'COMPLETED' ||
          newSession.status === 'FAILED';
        if (isFinal) {
          setDeployPollingActive(false);
        }
      }
    };

    // Immediate first poll, then on interval
    poll();
    const id = setInterval(poll, DEPLOY_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [
    deployPollingActive,
    deployPollingTick,
    mission_id,
    selectedSatellite?.value?.groupId,
    activeSetupId,
    pollSessionsForSatellite,
  ]);

  // ── Poll: ending sessions until backend removes them ──────────────────────
  useEffect(() => {
    if (!mission_id || !selectedSatellite?.value?.groupId) return;
    if (!sessions.some((session) => session.isDeleting)) return;
    let cancelled = false;

    const poll = async () => {
      if (!cancelled) await pollSessionsForSatellite();
    };

    const id = setInterval(poll, DEPLOY_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [mission_id, selectedSatellite?.value?.groupId, sessions, pollSessionsForSatellite]);

  // ── Derived ────────────────────────────────────────────────────────────────
  // SCHEDULED is an intermediate deploy state — always show it as DEPLOYING so the user
  // sees a clean Deploying → Running transition with no "Scheduled" in between.
  const displaySessionsForSatellite = useMemo<ValidateSession[]>(
    () => sessions.map((session) => ({
      ...session,
      status: session.status === 'SCHEDULED' ? 'DEPLOYING' as const : session.status,
    })),
    [sessions],
  );

  const setupsWithSessions = useMemo<ValidateSetup[]>(
    () => setups.map((setup) => ({
      ...setup,
      sessions: displaySessionsForSatellite.filter(
        (session) => session.setupId === setup.setupId && !session.setupIsDeleted,
      ),
    })),
    [setups, displaySessionsForSatellite],
  );

  const activeSetup = useMemo<ValidateSetup | null>(
    () => setupsWithSessions.find((s) => s.setupId === activeSetupId) ?? setupsWithSessions[0] ?? null,
    [setupsWithSessions, activeSetupId],
  );

  const displaySessionsForActiveSetup = useMemo<ValidateSession[]>(
    () => activeSetup?.sessions ?? [],
    [activeSetup],
  );

  const totalDurationMinutes = useMemo(
    () => displaySessionsForSatellite.reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0),
    [displaySessionsForSatellite],
  );

  const sessionFilterList = useMemo(
    () => getValidateSessionFilterList(displaySessionsForSatellite),
    [displaySessionsForSatellite],
  );

  const filteredSessions = useMemo<ValidateSession[]>(() => {
    const term = (allFilters?.searchFilter ?? '').trim().toLowerCase();
    let results = displaySessionsForSatellite;
    if (term) {
      results = results.filter((s) => {
        const statusLabel = SESSION_STATUS_LABEL[s.status]?.toLowerCase() ?? '';
        return (
          s.sessionName.toLowerCase().includes(term)               ||
          (s.setupName ?? '').toLowerCase().includes(term)         ||
          s.teamName.toLowerCase().includes(term)                  ||
          (s.testingEnvironment ?? '').toLowerCase().includes(term) ||
          statusLabel.includes(term)
        );
      });
    }

    const filtered = applySessionAdvanceFilters(results, allFilters?.advanceFilters);
    return [...filtered].sort((a, b) => {
      const aTime = a.startedAt ? new Date(a.startedAt).getTime() : 0;
      const bTime = b.startedAt ? new Date(b.startedAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [displaySessionsForSatellite, allFilters?.searchFilter, allFilters?.advanceFilters]);

  const hasRunningSession = useMemo(
    () => displaySessionsForSatellite.some((s) => s.status === 'RUNNING' || s.status === 'DEPLOYING'),
    [displaySessionsForSatellite],
  );

  // ── Actions ────────────────────────────────────────────────────────────────
  const onSatelliteChange = useCallback((option: SatelliteOption | null) => {
    const nextGroupId = option?.value?.groupId ?? '';
    const currentGroupId = selectedSatellite?.value?.groupId ?? '';
    if (nextGroupId && nextGroupId === currentGroupId) {
      // Re-selecting the same satellite should refresh setups/sessions instead of clearing UI.
      fetchSetupsAndPreserveSelection({ showErrors: true });
      fetchSessionsForSatellite({ showErrors: true });
      return;
    }
    setSelectedSatellite(option);
    setSetups([]);
    setSessions([]);
    setActiveSetupId(null);
    setLoadingSetups(true);
    setLoadingSessions(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      option?.value?.groupId ? next.set('sat', option.value.groupId) : next.delete('sat');
      next.delete('setup');
      return next;
    }, { replace: true });
    if (satStorageKey) {
      try {
        option?.value?.groupId
          ? window.sessionStorage.setItem(satStorageKey, option.value.groupId)
          : window.sessionStorage.removeItem(satStorageKey);
      } catch (err) { console.warn('sessionStorage write failed', err); }
    }
  }, [satStorageKey, setSearchParams, selectedSatellite?.value?.groupId, fetchSetupsAndPreserveSelection, fetchSessionsForSatellite]);

  const onSetupSelect = useCallback((setupId: string) => {
    setActiveSetupId(setupId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('setup', setupId);
      return next;
    }, { replace: true });
    if (setupStorageKey) {
      try { window.sessionStorage.setItem(setupStorageKey, setupId); }
      catch (err) { console.warn('sessionStorage write failed', err); }
    }
  }, [setupStorageKey, setSearchParams]);

  const onDeleteOpen = useCallback((session: ValidateSession) => {
    setDeleteTarget({
      type: 'session',
      id: session.sessionId,
      name: session.sessionName,
      setupId: session.setupId,
    });
  }, []);

  const onEndSessionOpen = useCallback((session: ValidateSession) => {
    setDeleteTarget({ type: 'end-session', id: session.sessionId, name: session.sessionName });
  }, []);

  const onSetupDeleteOpen = useCallback((setup: ValidateSetup) => {
    setDeleteTarget({ type: 'setup', id: setup.setupId, name: setup.setupName });
  }, []);

  const onDeleteConfirm = useCallback(async () => {
    if (!deleteTarget || !mission_id) return;
    const target = deleteTarget;
    const previousTargetSession = sessions.find((session) => session.sessionId === target.id);
    setDeleteTarget(null);
    try {
      if (target.type === 'end-session') {
        endingSessionIdsRef.current.add(target.id);
        setSessions((prev) => prev.map((sess) =>
          sess.sessionId === target.id ? { ...sess, isDeleting: true } : sess,
        ));
        await endSession(mission_id, target.id);
        await pollSessionsForSatellite();
      } else if (target.type === 'session') {
        const sessionSetupId =
          target.setupId ??
          sessions.find((sess) => sess.sessionId === target.id)?.setupId ??
          activeSetup?.setupId;

        if (!sessionSetupId) {
          showToastMessage('error', 'No setup found for this session');
          return;
        }

        const message = await deleteSession(mission_id, sessionSetupId, target.id);
        setSessions((prev) => prev.filter((sess) => sess.sessionId !== target.id));
        showToastMessage('success', message || 'Session deleted successfully');
      } else {
        const satelliteGroupId = selectedSatellite?.value?.groupId || '';
        const satelliteId = selectedSatellite?.value?.satId || '';
        const message = await deleteSetup(mission_id, target.id, satelliteGroupId, satelliteId);
        setSetups((prev) => prev.filter((s) => s.setupId !== target.id));
        setSessions((prev) => prev.map((sess) =>
          sess.setupId === target.id ? { ...sess, setupIsDeleted: true } : sess,
        ));
        setActiveSetupId(null);
        showToastMessage('success', message || 'Setup deleted successfully');
      }
    } catch (err) {
      if (target.type === 'end-session') {
        endingSessionIdsRef.current.delete(target.id);
        if (previousTargetSession) {
          setSessions((prev) => prev.map((session) =>
            session.sessionId === target.id ? previousTargetSession : session,
          ));
        }
      }
      if (!handleLockApiError(err)) {
        showToastMessage('error', extractApiError(err, 'Action failed. Please try again.'));
      }
    }
  }, [deleteTarget, mission_id, activeSetup, selectedSatellite, sessions, pollSessionsForSatellite]);

  const onDeleteCancel      = useCallback(() => setDeleteTarget(null), []);
  const onSearchFilterApply = useCallback((f: AllFilterType) => setAllFilters(f), []);
  const onStartNewSession   = useCallback(() => setIsCreateSessionModalOpen(true), []);

  /** Open session in Command Center. Completed validate sessions should land on Reports by default. */
  const onOpenRunningSession = useCallback(
    (session: ValidateSession) => {
      const isRunning = session.status === 'RUNNING';
      const isCompleted = session.status === 'COMPLETED';
      if (!isRunning && !isCompleted) return;
      const mName = mission_name ?? '';
      const mId   = mission_id ?? '';
      const satGroupId = selectedSatellite?.value?.groupId ?? '';
      const satId      = selectedSatellite?.value?.satId ?? selectedSatellite?.value?.groupId ?? '';
      if (!mName || !mId || !satGroupId || !satId) return;
      const base = process.env.PUBLIC_URL ?? '';
      const dashboard = isCompleted ? 'summary' : 'operate';
      const defaultTab = isCompleted ? 'reports' : 'hud';
      const path = `${base}/${mName}/${mId}/${dashboard}/${session.sessionId}/satGroupId/${satGroupId}/satellite/${satId}/${defaultTab}`;
      navigate(path);
    },
    [mission_name, mission_id, selectedSatellite, navigate],
  );

  const openCreateModal           = useCallback(() => setIsCreateModalOpen(true), []);
  const closeCreateModal          = useCallback(() => setIsCreateModalOpen(false), []);
  const openCreateSessionModal    = useCallback(() => setIsCreateSessionModalOpen(true), []);
  const closeCreateSessionModal   = useCallback(() => setIsCreateSessionModalOpen(false), []);

  const addPendingSession = useCallback((session: Pick<ValidateSession, 'sessionName' | 'teamName' | 'testingEnvironment' | 'setupId' | 'setupName'>) => {
    const pendingId = `pending-session-${Date.now()}`;
    setSessions((prev) => ([
      {
        sessionId: pendingId,
        sessionName: session.sessionName,
        status: 'DEPLOYING',
        startedAt: new Date().toISOString(),
        durationMinutes: 0,
        teamName: session.teamName,
        testingEnvironment: session.testingEnvironment,
        setupId: session.setupId,
        setupName: session.setupName,
        isPendingCreate: true,
      },
      ...prev,
    ]));
  }, []);

  const startDeployPolling = useCallback((setupId?: string) => {
    const targetSetupId = setupId || activeSetupId;
    // Capture baseline against the currently active setup.
    deployPollingSetupIdRef.current = targetSetupId;
    // Snapshot existing session IDs so we can identify the newly created one.
    deployPollingBaselineSessionIdsRef.current = new Set(
      sessions
        .filter((session) => !targetSetupId || session.setupId === targetSetupId)
        .map((s) => s.sessionId),
    );
    setDeployPollingActive(true);
    // Ensure polling restarts even if already active.
    setDeployPollingTick((t) => t + 1);
  }, [activeSetupId, sessions]);

  return {
    mission_id:    mission_id   ?? '',
    mission_name:  mission_name ?? '',
    satelliteOptions, selectedSatellite, loadingSatellites, onSatelliteChange,
    filteredSetups: setupsWithSessions, activeSetup, loadingSetups, loadingSessions, onSetupSelect, setSetups,
    filteredSessions, sessionsForActiveSetup: displaySessionsForActiveSetup, sessionsForSatellite: displaySessionsForSatellite, totalDurationMinutes, hasRunningSession, sessionFilterList,
    allFilters, setAllFilters,
    isCreateModalOpen, openCreateModal, closeCreateModal,
    groundStationOptions, loadingProviders,
    modelTypeOptions, loadingSetupConfig,
    isCreateSessionModalOpen, openCreateSessionModal, closeCreateSessionModal,
    testingEnvironmentOptions, loadingSessionConfig,
    deleteTarget, onDeleteOpen, onEndSessionOpen, onSetupDeleteOpen, onDeleteConfirm, onDeleteCancel,
    onSearchFilterApply, onStartNewSession, onOpenRunningSession, startDeployPolling, addPendingSession, refreshSessionsForSatellite, refreshSetupsForSatellite,
  };
}
